import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Switch } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { PopupContext, TableContext } from "../index";
import { getDeletePopup } from "../view/popup/PopupContainer";
import { changeReportScheduleActive, deleteReportSchedule, getReportSchedules } from "./reportService";
import { showSuccessToast } from "../validation/ToastContainer";

const SCHEDULE_REFRESH_INTERVAL_MS = 5000;

function getDateTimeFormat(dateValue) {
    if (!dateValue) {
        return "";
    }

    const date = new Date(dateValue);
    const yyyy = date.getFullYear();
    const MM = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const minuteValue = String(date.getMinutes()).padStart(2, "0");
    const hh = String(date.getHours() % 12 || 12).padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";

    return `${yyyy}-${MM}-${dd} ${hh}:${minuteValue} ${ampm}`;
}

function getScheduleRecurrenceSummary({
    recurrencePattern,
    recurEvery,
    selectedWeekDays,
    selectedMonthDay,
    selectedYearMonth,
    selectedYearDay
}) {
    if (recurrencePattern === "day") {
        return `Generate every ${recurEvery === 1 ? "day" : `${recurEvery} days`}`;
    }

    if (recurrencePattern === "week") {
        return `Generate every ${recurEvery === 1 ? "week" : `${recurEvery} weeks`} on ${selectedWeekDays || "-"}`;
    }

    if (recurrencePattern === "month") {
        return `Generate on day ${selectedMonthDay || "-"} for every ${recurEvery === 1 ? "month" : `${recurEvery} months`}`;
    }

    return `Generate every ${selectedYearMonth || "-"} ${selectedYearDay || ""}`.trim();
}

function normalizeScheduleResult(result) {
    return Array.isArray(result)
        ? result
        : Object.values(result || {}).filter((item) => item && typeof item === "object");
}

function ScheduledReportsTableRowSkeleton({ index }) {
    return (
        <tr key={index}>
            <td><Skeleton variant="text" width={80} height={24} /></td>
            <td><Skeleton variant="text" width="70%" height={24} /></td>
            <td><Skeleton variant="text" width="85%" height={24} /></td>
            <td><Skeleton variant="text" width={100} height={24} /></td>
            <td><Skeleton variant="text" width={110} height={24} /></td>
            <td><Skeleton variant="text" width={70} height={24} /></td>
            <td><Skeleton variant="text" width={140} height={24} /></td>
            <td><Skeleton variant="text" width={130} height={24} /></td>
            <td className="reports-schedule-table-cell-center">
                <Skeleton variant="rectangular" width={46} height={24} className="oct_skeleton-radius-12" />
            </td>
            <td className="reports-schedule-table-cell-center">
                <Skeleton variant="circular" width={24} height={24} />
            </td>
        </tr>
    );
}

function ScheduledReportsTable({ userRole }) {
    const { setPopup } = useContext(PopupContext);
    const { config } = useContext(TableContext);
    const [schedules, setSchedules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchSchedules = useCallback(async () => {
        if (!userRole) {
            setSchedules([]);
            setIsLoading(false);
            return;
        }

        const result = await getReportSchedules(userRole);
        setSchedules(normalizeScheduleResult(result));
        setIsLoading(false);
    }, [userRole]);

    useEffect(() => {
        fetchSchedules();
        const intervalId = window.setInterval(fetchSchedules, SCHEDULE_REFRESH_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [fetchSchedules]);

    const filteredSchedules = useMemo(() => {
        const query = (config.scheduleSearchTerm || "").trim().toLowerCase();
        const schedulesWithKeys = schedules.map((schedule, index) => ({
            ...schedule,
            key: schedule.id ?? `report-schedule-${index}`
        }));

        if (!query) {
            return schedulesWithKeys;
        }

        return schedulesWithKeys.filter((schedule) => {
            const recurrence = getScheduleRecurrenceSummary(schedule);
            const haystack = [
                schedule.name,
                schedule.description,
                schedule.emailTo,
                schedule.type,
                schedule.range,
                schedule.recurrencePattern,
                recurrence,
                getDateTimeFormat(schedule.lastScheduledTime)
            ].join(" ").toLowerCase();

            return haystack.includes(query);
        });
    }, [config.scheduleSearchTerm, schedules]);

    const handleToggleChange = (scheduleId, currentActive) => {
        const nextActive = !currentActive;
        setSchedules((currentSchedules) => currentSchedules.map((schedule) => (
            String(schedule.id) === String(scheduleId)
                ? { ...schedule, active: nextActive }
                : schedule
        )));

        changeReportScheduleActive(scheduleId, nextActive)
            .then(() => showSuccessToast("Report Schedule is Updated", "Report Schedule"));
    };

    const handleDelete = async (id) => {
        await deleteReportSchedule(id);
        showSuccessToast("Report Schedule is Deleted", "Delete Schedule");
        fetchSchedules();
    };

    const handleClickDeleteOpen = (id) => {
        setPopup(getDeletePopup(
            "Are you sure you want to delete this schedule?",
            "Delete Schedule",
            () => handleDelete(id),
            undefined,
            "Delete Schedule"
        ));
    };

    return (
        <>
            <div className="reports-schedule-table-wrap">
                {!isLoading && filteredSchedules.length === 0 ? (
                    <div className="oct_empty-state-v2">
                        <div className="oct_empty-state-title">No schedules found</div>
                        <div>Try adjusting your search to find what you're looking for.</div>
                    </div>
                ) : (
                    <table className="reports-schedule-table">
                        <thead>
                            <tr>
                                <th>Start Time</th>
                                <th>Report</th>
                                <th>Email</th>
                                <th>Type</th>
                                <th>Date Range</th>
                                <th>Pattern</th>
                                <th>Recurrence</th>
                                <th>Last Execution</th>
                                <th>Enable</th>
                                <th>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from(new Array(10)).map((_, index) => (
                                    <ScheduledReportsTableRowSkeleton key={index} index={index} />
                                ))
                            ) : (
                                filteredSchedules.map((schedule) => (
                                    <tr key={schedule.key}>
                                        <td>{new Date(schedule.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</td>
                                        <td>{schedule.name}</td>
                                        <td>{schedule.emailTo || "-"}</td>
                                        <td>{schedule.type}</td>
                                        <td>{schedule.range}</td>
                                        <td>{schedule.recurrencePattern}</td>
                                        <td>{getScheduleRecurrenceSummary(schedule)}</td>
                                        <td>{getDateTimeFormat(schedule.lastScheduledTime) || "-"}</td>
                                        <td className="reports-schedule-table-cell-center">
                                            <Switch
                                                checked={Boolean(schedule.active)}
                                                onChange={() => handleToggleChange(schedule.id, schedule.active)}
                                                disabled={!schedule.id}
                                            />
                                        </td>
                                        <td className="reports-schedule-table-cell-center">
                                            <button
                                                type="button"
                                                className="reports-schedule-table-delete"
                                                aria-label="Delete Schedule"
                                                title="Delete Schedule"
                                                onClick={() => handleClickDeleteOpen(schedule.id)}
                                                disabled={!schedule.id}
                                            >
                                                <DeleteOutlineOutlinedIcon fontSize="small" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
}

function mapStateToProps(state) {
    return {
        userRole: state.auth?.userRole?.name
    };
}

export default connect(mapStateToProps)(ScheduledReportsTable);
